import { ChangeDetectionStrategy, Component } from '@angular/core';

import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormlyModule } from '@ngx-formly/core';
import { SelectV2Module } from '@gea/digital-ui-lib';
import { FormFieldsService } from '../app/services/form-fields.service';
import { NgClass } from '@angular/common';
import { typedOptionsToArray } from '../app/services/FormlyHelper';
import { TypedOptionEntry } from '../app/pages/products/product.model';

@Component({
  selector: 'formly-geaui-select-v2',
  imports: [ReactiveFormsModule, FormlyModule, SelectV2Module, NgClass],
  template: `
    <geaui-select-v2
      [alwaysFloatLabel]="true"
      [label]="props['label'] ?? ''"
      [options]="resolvedOptions"
      [disabled]="isDisabled"
      [group]="formGroup"
      [ngClass]="props['errorMessage'] ? 'red-border' : ''"
      [formName]="field.key?.toString()"
      [formlyAttributes]="field"
      (selectChanged)="updateField()"></geaui-select-v2>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeaUiSelectV2TypeComponent extends FieldType<FieldTypeConfig> {
  constructor(private readonly formlyService: FormFieldsService) {
    super();
  }

  get formGroup(): FormGroup {
    return this.form as FormGroup;
  }

  /**
   * Resolved options array.
   * Prefers TypedOptions (new PPM format) over legacy dropdownOptions.
   * Returns { name, value } to match SelectOption / EnumOption / StringOption
   * from @gea/digital-ui-lib (all use `name` as the display field, not `label`).
   */
  get resolvedOptions(): { name: string; value: unknown; disabled: boolean }[] {
    const typedOptions = this.props['typedOptions'] as Record<string, TypedOptionEntry> | null | undefined;
    if (typedOptions && typeof typedOptions === 'object') {
      return typedOptionsToArray(typedOptions);
    }
    return this.props['dropdownOptions'] ?? [];
  }

  get isDisabled(): boolean {
    // props.disabled is set by applyValueEntityToField (IsEnabled=false or ≤1 option).
    // The opts.length<=1 guard is a legacy fallback for non-ValueEntity paths.
    const opts = this.resolvedOptions;
    return !!(this.props['disabled'] || (Array.isArray(opts) && opts.length <= 1));
  }

  updateField(): void {
    if (this.field.key) {
      const newVal = this.formGroup.get(this.field.key.toString())?.value;
      this.formlyService.trackField({ key: this.field.key.toString(), value: newVal });
    }
  }
}
